// src/App.jsx (wrap app routes with the provider so fetching starts post-auth)
import React, { Suspense, lazy, useMemo } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useNavVariant } from "./utils/navigation/useNavVariant";
import { ProtectedRoute } from "./components/authentication/ProtectedRoute";
import NavBar from "./components/navigation/NavBar";
import MDNavBar from "./components/navigation/MDNavBar";
import HomePage from "./pages/HomePage";
import ForgotPasswordRequest from "./components/authentication/ForgotPasswordRequest";
import ResetPassword from "./components/authentication/ResetPassword";
import { firebaseApp } from "./firebase/firebaseApp";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { useAuth } from "./context/AuthContext";
import LevelSelectionPage from "./pages/LevelSelectionPage";
import { CompatibleRoute } from "./components/navigation/CompatibleRoute";
import RecordingForcedSpirometryRouter from "./routes/RecordingForcedSpirometryRouter";
// import RecordingExertionTestRouter from "./routes/RecordingExertionTestRouter";
import BottomNavBar from "./components/navigation/BottomNavBar";
import SettingsPage from "./pages/SettingsPage";
import CreateAccountPage from "./pages/CreateAccountPage";
import CheckoutPage from "./pages/CheckoutPage";
import LoginPage from "./pages/LoginPage";
import { ActivityFeedProvider } from "./context/ActivityFeedContext";
import PaymentSelectPage from "./pages/PaymentSelectPage";
import RecordingExertionRecoveryRouter from "./routes/RecordingExertionRecoveryRouter";
import { UserDocProvider } from "./context/UserDocContext";
import TopSegmentedNav from "./components/navigation/TopSegmentedNav";
import TrendPage from "./pages/TrendPage";
import { AccessControlProvider } from "./context/AccessControlContext";
import ActivityPage from "./pages/ActivityPage";
import InfoPage from "./pages/InfoPage";
import { ConsentProvider } from "./consent/ConsentContext";
import RouteTracking from "./analytics/routeTracking";
import { RedirectIfAuthed } from "./components/navigation/RedirectIfAuthed";
// import RecordingNormalBreathingRouter from "./routes/RecordingNormalBreathingRouter";
import SignupPage from "./pages/SignupPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import { AdminRoute } from "./components/admin/AdminRoute";
import AdminInspectMeasurementsPage from "./pages/admin/AdminInspectMeasurementsPage";
import AdminTrendsPage from "./pages/admin/AdminTrendsPage";

const LandingPage = lazy(() => import("./pages/LandingPage"));
const CompatibilityPage = lazy(() => import("./pages/CompatibilityPage"));

export default function App() {
  const auth = useMemo(() => getAuth(firebaseApp), []);
  const storage = useMemo(() => getStorage(firebaseApp), []);
  const { user } = useAuth();
  const navVariant = useNavVariant();
  const RESULTS_NAMESPACE =
    process.env.REACT_APP_RESULTS_NAMESPACE || "default";

  return (
    <ConsentProvider
      measurementId={process.env.REACT_APP_GA_MEASUREMENT_ID || "G-YL7LYF7GRW"}
      hotjarId={6559252}
      hotjarSv={6}
    >
      <div className="h-[100dvh] flex flex-col typography">
        {navVariant === "main" ? <NavBar /> : <MDNavBar />}
        {/* NEW: pinned segmented nav below the NavBar */}
        <TopSegmentedNav />
        <UserDocProvider
        //  debug
        >
          {/* Provide the feed globally once the user is known */}
          <ActivityFeedProvider
            storage={storage}
            uid={user?.uid || null}
            namespace={RESULTS_NAMESPACE}
            pollMs={15000}
            // debug
          >
            <AccessControlProvider>
              <div
                id="app-scroll"
                className="flex flex-col items-center justify-start h-full bg-background overflow-y-scroll"
              >
                <Suspense fallback={<div className="m-auto">Loading…</div>}>
                  <Routes>
                    {/* Public routes */}
                    <Route
                      path="/"
                      element={
                        <RedirectIfAuthed user={user}>
                          <LandingPage />
                        </RedirectIfAuthed>
                      }
                    />
                    <Route path="/payment" element={<PaymentSelectPage />} />
                    <Route
                      path="/auth/create-account"
                      element={<CreateAccountPage />}
                    />
                    <Route path="/auth/login" element={<LoginPage />} />
                    <Route path="/auth/signup" element={<SignupPage />} />

                    <Route path="/pay/checkout" element={<CheckoutPage />} />
                    <Route
                      path="/auth/forgot-password"
                      element={<ForgotPasswordRequest />}
                    />
                    <Route
                      path="/auth/reset-password"
                      element={<ResetPassword />}
                    />
                    <Route path="/info" element={<InfoPage />} />

                    {/* Protected */}
                    {/* <Route
                path="/form"
                element={
                  <ProtectedRoute>
                    <RequirePayment>
                      <FormPage />
                    </RequirePayment>
                  </ProtectedRoute>
                }
              /> */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <SettingsPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/home"
                      element={
                        <ProtectedRoute>
                          <HomePage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/home/trends"
                      element={
                        <ProtectedRoute>
                          <TrendPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/home/activity"
                      element={
                        <ProtectedRoute>
                          <ActivityPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/recording"
                      element={
                        <ProtectedRoute>
                          <LevelSelectionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/recording/forced-spirometry/*"
                      element={
                        <CompatibleRoute>
                          <ProtectedRoute>
                            <RecordingForcedSpirometryRouter
                              auth={auth}
                              storage={storage}
                              user={user}
                              variant={"RecordingForcedSpirometry"}
                              filePathTag="breatheultra/"
                              usePublicAssets={true}
                            />
                          </ProtectedRoute>
                        </CompatibleRoute>
                      }
                    />

                    {/* <Route
                      path="/recording/normal-breathing/*"
                      element={
                        <CompatibleRoute>
                          <ProtectedRoute>
                            <RecordingNormalBreathingRouter
                              auth={auth}
                              storage={storage}
                              user={user}
                              variant={"RecordingNormalBreathing"}
                              filePathTag="breatheultra/"
                              usePublicAssets={true}
                              debugTracking={true}
                            />
                          </ProtectedRoute>
                        </CompatibleRoute>
                      }
                    /> */}
                    {/* <Route
                      path="/recording/exertion-test/*"
                      element={
                        <CompatibleRoute>
                          <ProtectedRoute>
                            <RecordingExertionTestRouter
                              auth={auth}
                              storage={storage}
                              user={user}
                              variant={"RecordingExertionTest"}
                              filePathTag="breatheultra/"
                            />
                          </ProtectedRoute>
                        </CompatibleRoute>
                      }
                    /> */}
                    <Route
                      path="/recording/exertion-recovery/*"
                      element={
                        <CompatibleRoute>
                          <ProtectedRoute>
                            <RecordingExertionRecoveryRouter
                              auth={auth}
                              storage={storage}
                              user={user}
                              variant={"RecordingExertionRecovery"}
                              filePathTag="breatheultra/"
                              usePublicAssets={true}
                            />
                          </ProtectedRoute>
                        </CompatibleRoute>
                      }
                    />
                    <Route
                      path="/compatibility"
                      element={<CompatibilityPage />}
                    />

                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute>
                          <AdminRoute>
                            <AdminUsersPage />
                          </AdminRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/inspect"
                      element={
                        <ProtectedRoute>
                          <AdminRoute>
                            <AdminInspectMeasurementsPage />
                          </AdminRoute>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/trends"
                      element={
                        <AdminRoute>
                          <AdminTrendsPage />
                        </AdminRoute>
                      }
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </div>
            </AccessControlProvider>
          </ActivityFeedProvider>
        </UserDocProvider>

        {navVariant === "main" ? <BottomNavBar /> : null}
      </div>
      <RouteTracking />
    </ConsentProvider>
  );
}
